/**
 * Plans Page — catalogo de planes de licenciamiento (SUPER_ADMIN).
 *
 * Vista para revisar y editar el catalogo de planes (PILOTO, NORMAL).
 * Permite ajustar precio, dias de trial, periodo de gracia, y limites
 * sin tocar codigo ni base de datos.
 */

import { useState } from 'react'
import { CreditCard, Loader2, Edit2, Save, X } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/presentation/components/ui/Table'
import { usePlans, useUpdatePlan } from '@/application/subscriptions/useSubscriptions'
import { toast } from '@/lib/toast'
import { getErrorMessage } from '@/lib/api'
import type { Plan } from '@/domain/subscription/types'

const formatCop = (n: number) =>
  new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  }).format(n)

const formatLimit = (n: number | null) => (n === null ? 'Ilimitado' : n.toLocaleString('es-CO'))

export default function PlansPage() {
  const { data, isLoading } = usePlans()
  const updateMutation = useUpdatePlan()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draft, setDraft] = useState<Partial<Plan>>({})

  const startEdit = (plan: Plan) => {
    setEditingId(plan.id)
    setDraft({
      monthlyPriceCop: plan.monthlyPriceCop,
      trialDays: plan.trialDays,
      gracePeriodDays: plan.gracePeriodDays,
      maxUsers: plan.maxUsers,
      maxStudiesPerMonth: plan.maxStudiesPerMonth,
      maxStorageGb: plan.maxStorageGb,
    })
  }

  const cancelEdit = () => {
    setEditingId(null)
    setDraft({})
  }

  const saveEdit = () => {
    if (!editingId) return
    updateMutation.mutate(
      { id: editingId, data: draft },
      {
        onSuccess: () => {
          toast.success('Plan actualizado')
          cancelEdit()
        },
        onError: (err) => toast.error(getErrorMessage(err)),
      }
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <CreditCard className="h-6 w-6" />
          Catalogo de planes
        </h1>
        <p className="text-[var(--foreground-muted)]">
          Define el precio, periodo de prueba y limites operativos. Los cambios
          aplican inmediatamente a las nuevas suscripciones; los pagos historicos
          conservan el monto del momento del cobro.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Planes activos</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="h-6 w-6 animate-spin text-[var(--primary)]" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Plan</TableHead>
                  <TableHead>Precio mensual</TableHead>
                  <TableHead>Trial (dias)</TableHead>
                  <TableHead>Gracia (dias)</TableHead>
                  <TableHead>Max usuarios</TableHead>
                  <TableHead>Max estudios/mes</TableHead>
                  <TableHead>Max storage (GB)</TableHead>
                  <TableHead className="w-[150px]">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.plans.map((plan) => {
                  const isEditing = editingId === plan.id
                  return (
                    <TableRow key={plan.id}>
                      <TableCell>
                        <div className="font-medium">{plan.name}</div>
                        <div className="text-xs text-[var(--foreground-muted)]">
                          {plan.code} {plan.isPilot ? '· piloto' : ''}
                        </div>
                      </TableCell>
                      <TableCell>
                        {isEditing ? (
                          <Input
                            type="number"
                            min={0}
                            value={draft.monthlyPriceCop ?? 0}
                            onChange={(e) =>
                              setDraft({
                                ...draft,
                                monthlyPriceCop: Number(e.target.value),
                              })
                            }
                            className="w-32"
                          />
                        ) : (
                          formatCop(plan.monthlyPriceCop)
                        )}
                      </TableCell>
                      <TableCell>
                        {isEditing ? (
                          <Input
                            type="number"
                            min={0}
                            max={365}
                            value={draft.trialDays ?? 0}
                            onChange={(e) =>
                              setDraft({ ...draft, trialDays: Number(e.target.value) })
                            }
                            className="w-20"
                          />
                        ) : (
                          plan.trialDays
                        )}
                      </TableCell>
                      <TableCell>
                        {isEditing ? (
                          <Input
                            type="number"
                            min={0}
                            max={90}
                            value={draft.gracePeriodDays ?? 0}
                            onChange={(e) =>
                              setDraft({
                                ...draft,
                                gracePeriodDays: Number(e.target.value),
                              })
                            }
                            className="w-20"
                          />
                        ) : (
                          plan.gracePeriodDays
                        )}
                      </TableCell>
                      <TableCell>{formatLimit(plan.maxUsers)}</TableCell>
                      <TableCell>{formatLimit(plan.maxStudiesPerMonth)}</TableCell>
                      <TableCell>{formatLimit(plan.maxStorageGb)}</TableCell>
                      <TableCell>
                        {isEditing ? (
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={saveEdit}
                              disabled={updateMutation.isPending}
                              title="Guardar"
                            >
                              <Save className="h-4 w-4 text-green-600 dark:text-green-400" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={cancelEdit} title="Cancelar">
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => startEdit(plan)}
                            title="Editar"
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
